import type { Color } from '@xox/shared'
import { Skater } from '../../ice/art/Skater'
import { Yeti } from '../../ice/art/Yeti'
import type { GameInfo } from '../../lib/games'
import { RobotFace } from './RobotFace'

interface Props {
  game: GameInfo['to']
  /** Width class from the caller; the logo is square. */
  className?: string
  /** Shirt color of the skater on the ice logo (the viewer's profile color). */
  color?: Color
  /** Icon size (menu rows): the board or ice block alone, no characters spilling over the edge. */
  compact?: boolean
}

/**
 * Drawn game logos: little scenes built from the games' own artwork, so they look the same on
 * every device. Decorative — the title next to them carries the name.
 */
export function GameLogo({ game, className = '', color = 'sky', compact = false }: Props) {
  return (
    <span className={`relative inline-block aspect-square shrink-0 ${className}`} aria-hidden>
      {game === '/xox' ? (
        <XoxScene compact={compact} />
      ) : game === '/c4' ? (
        <C4Scene compact={compact} />
      ) : game === '/cycles' ? (
        <CyclesScene compact={compact} />
      ) : game === '/dungeon' ? (
        <DungeonScene />
      ) : (
        <IceScene color={color} compact={compact} />
      )}
    </span>
  )
}

/** A stone archway over a dark stair, a torch glowing beside it, a small hero at the threshold. */
function DungeonScene() {
  return (
    <svg viewBox="0 0 40 40" className="size-full" aria-hidden>
      <rect x="3" y="3" width="34" height="34" rx="6" fill="var(--dg-wall)" />
      <path d="M9 36 V18 A11 11 0 0 1 31 18 V36 Z" fill="var(--dg-floor)" />
      <path d="M13 36 V20 A7 7 0 0 1 27 20 V36 Z" fill="var(--dg-dark)" />
      <path d="M15 36 H25 M16 32 H24 M17 28 H23" stroke="var(--dg-stairs)" strokeWidth="1.6" />
      {/* torch */}
      <rect x="32.5" y="15" width="1.6" height="7" fill="var(--dg-wood)" />
      <ellipse cx="33.3" cy="13.5" rx="2.4" ry="3" fill="var(--dg-torch)" />
      <ellipse cx="33.3" cy="14" rx="1.1" ry="1.6" fill="var(--dg-flame)" />
      {/* hero */}
      <rect x="18" y="26" width="4" height="6" rx="1" fill="var(--dg-hero)" />
      <circle cx="20" cy="24.5" r="2" fill="var(--dg-hero)" />
      <path d="M22.5 27 L26 24" stroke="var(--dg-steel)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}

/** A dark arena with two light trails closing in on each other; a GRID bot's face in the corner. */
function CyclesScene({ compact }: { compact: boolean }) {
  return (
    <>
      <svg viewBox="0 0 40 40" className="size-full" aria-hidden>
        <rect x="3" y="3" width="34" height="34" rx="7" fill="var(--cycles-bg)" />
        <path
          d="M8 8 H20 V16 H12 V24"
          fill="none"
          stroke="var(--shirt-sky)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M32 32 H24 V22 H30 V14"
          fill="none"
          stroke="var(--shirt-amber)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="10" y="22" width="4" height="4" rx="1" fill="white" />
        <rect x="28" y="12" width="4" height="4" rx="1" fill="white" />
      </svg>
      {!compact && (
        <RobotFace
          palette="robot-c4"
          className="absolute -right-[10%] -bottom-[4%] w-[42%] drop-shadow-md"
        />
      )}
    </>
  )
}

/** The blue frame with a few discs stacked and one still falling; DROP-9000 peeks over the edge. */
function C4Scene({ compact }: { compact: boolean }) {
  const holes: [number, number, 'X' | 'O' | null][] = [
    [11, 31, 'X'],
    [20, 31, 'O'],
    [29, 31, 'X'],
    [11, 23, 'O'],
    [20, 23, 'X'],
    [29, 23, null],
    [11, 15, 'X'],
    [20, 15, null],
    [29, 15, null],
  ]
  return (
    <>
      <svg viewBox="0 0 40 40" className="size-full" aria-hidden>
        <rect x="3" y="9" width="34" height="29" rx="5" fill="var(--c4-frame)" />
        <rect x="3" y="34" width="34" height="4" rx="2" fill="var(--c4-frame-dark)" />
        {holes.map(([cx, cy, disc]) => (
          <g key={`${cx}-${cy}`}>
            <circle cx={cx} cy={cy} r="3.6" fill="var(--c4-hole)" />
            {disc && (
              <circle
                cx={cx}
                cy={cy}
                r="3"
                fill={disc === 'X' ? 'var(--c4-red)' : 'var(--c4-yellow)'}
              />
            )}
          </g>
        ))}
        {/* a disc on its way down */}
        <circle cx="29" cy="4" r="3" fill="var(--c4-yellow)" />
      </svg>
      {!compact && (
        <RobotFace
          palette="robot-c4"
          className="absolute -top-[6%] -left-[12%] w-[44%] drop-shadow-md"
        />
      )}
    </>
  )
}

/** A tilted board with a bold X and O, one mark already fading, and XOX-9000 peeking in. */
function XoxScene({ compact }: { compact: boolean }) {
  return (
    <>
      <svg viewBox="0 0 40 40" className="size-full" aria-hidden>
        <g transform="rotate(-8 20 20)">
          <rect x="4" y="6" width="32" height="32" rx="7" fill="white" opacity=".95" />
          <path
            d="M14.5 9 V35 M25.5 9 V35 M7 17 H33 M7 27 H33"
            stroke="var(--ice-rock-light)"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          {/* fresh X */}
          <path
            d="M8 11 L13 16 M13 11 L8 16"
            stroke="var(--color-x)"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
          {/* fresh O */}
          <circle cx="20" cy="22" r="3.2" fill="none" stroke="var(--color-o)" strokeWidth="2.6" />
          {/* the vanishing X */}
          <path
            d="M27.5 29 L32 33.5 M32 29 L27.5 33.5"
            stroke="var(--color-x)"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeDasharray="1.5 1.8"
            opacity=".45"
          />
          {/* an O about to land */}
          <circle
            cx="9.5"
            cy="31"
            r="3"
            fill="none"
            stroke="var(--color-o)"
            strokeWidth="2.2"
            opacity=".35"
          />
        </g>
      </svg>
      {!compact && <RobotFace className="absolute -top-[4%] -right-[10%] w-[46%] drop-shadow-md" />}
    </>
  )
}

/** An ice block with a door, the skater mid-slide and the yeti watching from behind. */
function IceScene({ color, compact }: { color: Color; compact: boolean }) {
  return (
    <>
      <svg viewBox="0 0 40 40" className="size-full" aria-hidden>
        <rect
          x="4"
          y="9"
          width="32"
          height="27"
          rx="6"
          fill="var(--ice-surface)"
          stroke="var(--ice-surface-deep)"
          strokeWidth="1.5"
        />
        <path
          d="M8 14 Q8 12 10 12 H20"
          fill="none"
          stroke="var(--ice-sheen)"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <path d="M11 30 L15 20 M20 33 L23 25" stroke="var(--ice-crack)" strokeWidth="1" />
        {/* door */}
        <rect x="27" y="14" width="6.5" height="10" rx="1.5" fill="var(--ice-wood)" />
        <rect x="27" y="14" width="6.5" height="2" rx="1" fill="var(--ice-wood-dark)" />
        <circle cx="31.8" cy="19.5" r=".9" fill="var(--ice-brass)" />
        {/* hole */}
        <ellipse cx="11" cy="31" rx="4" ry="2.2" fill="var(--ice-hole)" />
        <ellipse cx="11" cy="30.4" rx="4" ry="1.6" fill="var(--ice-hole-rim)" />
      </svg>
      {!compact && (
        <>
          <Yeti facing="right" className="absolute -left-[8%] top-[6%] w-[36%]" />
          <Skater color={color} lean="right" className="absolute bottom-[16%] left-[32%] h-[58%]" />
        </>
      )}
    </>
  )
}
